// Opens a dedicated subscription for cost + contribution on the PERMANENT ledger
// retention class (FH-5; sinceSeq:0 — the mirror needs full history to genesis, and the
// ledger is never purged so there is no floor/reset) and accumulates only events that are
// signature-verified AND signed by an allowlisted commons issuer. The issuer allowlist is
// the trust gate; the ledger class carries the deployment's cost/contribution stream
// (selected by class, not by cell filter — spec 6 §1).
//
// Events arrive as the FULL signed envelope on `evt.json`: the lexicon type is
// `evt.json.$type`, the signer (host) DID is `evt.json.identityId`, and the record
// fields (period/spendFiat… or beneficiary/hostValue…) live under `evt.json.payload`.
//
// Verification RACE: this feed and the global identity subscription (which folds
// declareKeys into the shared directory) backfill independently. A commons event
// can arrive BEFORE its signer's genesis has folded, so the subscription stamps it
// verified=false (signer key not yet resolvable). Dropping it permanently would
// strand the whole mirror behind a backfill ordering accident. So events whose ONLY
// defect is an unresolved signer (signer IS allowlisted) are BUFFERED and re-verified
// against the live directory as the identity backfill catches up.
import { verifyParsed, type ReceivedEvent } from "@beacon/client-core";
import type { CostEvent, ContribEvent } from "./mirror.js";

export type CommonsState = { costs: CostEvent[]; contribs: ContribEvent[] };

type Sub = AsyncIterable<ReceivedEvent> & { close(): Promise<void> };
type SubClient = {
  subscribe(f: { cells?: string[]; types: string[]; sinceSeq?: number; class?: "ledger" }): Sub;
  // Used to re-verify buffered events once the signer's genesis folds.
  directory?: { resolve(did: string): Promise<{ signingKey: string } | null> };
};

const COST = "app.aid.account.cost";
const CONTRIB = "app.aid.account.contribution";
// Re-verify the pending buffer on a bounded back-off (ms) covering the identity
// backfill window; after the last attempt, give up on still-unresolved events.
const REVERIFY_DELAYS = [800, 1600, 3200, 6400, 12800, 25600];

export class CommonsFeed {
  #client: SubClient;
  #cell: string;
  #issuers: string[];
  #onChange: (s: CommonsState) => void;
  #verify: (evt: ReceivedEvent) => Promise<boolean>;
  #sub?: Sub;
  #costs: CostEvent[] = [];
  #contribs: ContribEvent[] = [];
  #pending: ReceivedEvent[] = []; // allowlisted but signer not yet resolvable
  #warned = new Set<string>(); // signer DIDs already warned about (dedupe)
  #reverifyAttempt = 0;
  #reverifyTimer?: ReturnType<typeof setTimeout>;

  constructor(
    client: SubClient,
    opts: {
      cell: string;
      issuers: string[];
      onChange: (s: CommonsState) => void;
      // Injectable for tests; defaults to verifyParsed against the client directory.
      verify?: (evt: ReceivedEvent) => Promise<boolean>;
    },
  ) {
    this.#client = client;
    this.#cell = opts.cell;
    this.#issuers = opts.issuers;
    this.#onChange = opts.onChange;
    this.#verify =
      opts.verify ??
      (async (evt) => {
        const v = await verifyParsed(evt.cbor, async (did) => {
          const keys = await this.#client.directory?.resolve(did);
          return keys?.signingKey ?? null;
        });
        return v.ok && v.verified;
      });
  }

  /** The envelope signer DID (the host identityId). */
  static signerOf(evt: { json?: Record<string, unknown> }): string | undefined {
    return evt.json?.identityId as string | undefined;
  }

  static isTrusted(evt: { verified: boolean; json?: Record<string, unknown> }, issuers: string[]): boolean {
    if (!evt.verified) return false;
    const signer = CommonsFeed.signerOf(evt);
    return !!signer && issuers.includes(signer);
  }

  start(): void {
    if (this.#sub) return;
    // FH-5: read the PERMANENT ledger class (full history to genesis, no floor/reset),
    // not a cell-filtered ephemeral read. #cell remains the configured commonsCell for
    // the transparency surface; the ledger stream carries the deployment's events.
    const sub = this.#client.subscribe({ types: [COST, CONTRIB], class: "ledger", sinceSeq: 0 });
    this.#sub = sub;
    void this.#pump(sub);
  }

  async stop(): Promise<void> {
    if (this.#reverifyTimer) {
      clearTimeout(this.#reverifyTimer);
      this.#reverifyTimer = undefined;
    }
    if (this.#sub) {
      await Promise.resolve(this.#sub.close()).catch(() => {});
      this.#sub = undefined;
    }
  }

  state(): CommonsState {
    return { costs: [...this.#costs], contribs: [...this.#contribs] };
  }

  async #pump(sub: Sub): Promise<void> {
    for await (const evt of sub) {
      if (CommonsFeed.isTrusted(evt, this.#issuers)) {
        if (this.#admit(evt)) this.#onChange(this.state());
        continue;
      }
      const signer = CommonsFeed.signerOf(evt);
      // Only defect is an unresolved signer key (signer IS allowlisted) → buffer and
      // re-verify later. A signer outside the allowlist is a real reject → warn+drop.
      if (signer && this.#issuers.includes(signer) && !evt.verified) {
        this.#pending.push(evt);
        this.#scheduleReverify();
      } else {
        this.#warnDropped(evt);
      }
    }
  }

  /** Push a verified+allowlisted event's record into the right bucket. */
  #admit(evt: ReceivedEvent): boolean {
    const type = (evt.json as { $type?: string }).$type;
    const record = (evt.json.payload ?? {}) as Record<string, unknown>;
    if (type === COST) this.#costs.push(record as unknown as CostEvent);
    else if (type === CONTRIB) this.#contribs.push(record as unknown as ContribEvent);
    else return false;
    return true;
  }

  #scheduleReverify(): void {
    if (this.#reverifyTimer || this.#reverifyAttempt >= REVERIFY_DELAYS.length) return;
    const delay = REVERIFY_DELAYS[this.#reverifyAttempt];
    this.#reverifyTimer = setTimeout(() => {
      this.#reverifyTimer = undefined;
      void this.reverifyPending();
    }, delay);
  }

  /** Re-verify buffered events against the now-fuller directory. Admits any that
   *  now verify; reschedules while some remain unresolved; gives up after the last
   *  back-off (a permanently unresolvable signer means its genesis is missing). */
  async reverifyPending(): Promise<void> {
    if (this.#pending.length === 0) return;
    this.#reverifyAttempt++;
    const stillPending: ReceivedEvent[] = [];
    let changed = false;
    for (const evt of this.#pending) {
      const verified = await this.#verify(evt).catch(() => false);
      if (verified) {
        if (this.#admit(evt)) changed = true;
      } else {
        stillPending.push(evt);
      }
    }
    this.#pending = stillPending;
    if (changed) this.#onChange(this.state());
    if (this.#pending.length === 0) return;
    if (this.#reverifyAttempt >= REVERIFY_DELAYS.length) {
      for (const evt of this.#pending) this.#warnDropped(evt); // genesis never showed up
      this.#pending = [];
      return;
    }
    this.#scheduleReverify();
  }

  // A commons event was rejected by the trust filter. Without this, every dropped
  // event is indistinguishable from no events at all ("No commons activity yet") —
  // surface WHY (unresolved signer key vs a signer DID not in BEACON_COMMONS_ISSUERS),
  // once per offending signer.
  #warnDropped(evt: { verified: boolean; json?: Record<string, unknown> }): void {
    const signer = CommonsFeed.signerOf(evt);
    const key = signer ?? "(unknown signer)";
    if (this.#warned.has(key)) return;
    this.#warned.add(key);
    // Allowlist mismatch is checked FIRST: a signer not in the allowlist is rejected
    // regardless of its signature, and that's the actionable config error (an exact
    // DID mismatch — a stray char — looks like everything else being "fine"). Only a
    // signer that IS allowlisted but never resolved is a genesis/relay problem.
    const reason =
      !signer || !this.#issuers.includes(signer)
        ? `signer not in BEACON_COMMONS_ISSUERS [${this.#issuers.join(", ") || "empty"}] — check the DID matches exactly`
        : "signature unverified (signer key not resolvable after retries — is its genesis declareKeys on the relay?)";
    console.warn(`commons: dropping event from ${key}: ${reason}`);
  }
}
